import { useState } from "react";
import { TableRow } from "../../../../components/tables";
import { getTime } from "../helpers";
import { api } from "../../../../services/axios";
import { DesignProductTemplate } from "../../../../utils/types";
import { toast } from "sonner";
import { groupByMultipleKeys } from "../../../../utils";

export function DesignProductTemplatesTable({
  templates,
  page,
  previousPage,
  nextPage,
}: {
  templates: DesignProductTemplate[];
  page: number;
  previousPage: () => void;
  nextPage: () => void;
}) {
  const [selected, setSelected] = useState<string[]>([]);

  const runTemplate = (licenseId: string) => {
    return api
      .put(`product-templates/${licenseId}`, {
        updated_at: new Date().getTime().toString,
        auto_run: true,
      })
      .then(() => toast.success("starting", { duration: 5000 }))
      .catch((e) => toast.error(e.message, { duration: 5000 }));
  };

  const deleteTemplate = async (licenseId: string) => {
    return api
      .delete(`product-templates/${licenseId}`)
      .then(() => toast.success("deleted", { duration: 5000 }))
      .catch((e) => toast.error(e.message, { duration: 5000 }));
  };

  function handleSelect(id: string) {
    if (selected.includes(id)) {
      const removed = selected.filter((x) => x !== id);
      setSelected(removed);
    } else {
      setSelected([...selected, id]);
    }
  }

  async function deleteAllSelectedLicensing() {
    for (let i = 0; i < selected.length; i++) {
      await deleteTemplate(selected[i]);
      toast.success(selected[i], { duration: 5000 });
    }
    setSelected([]);
    toast.success("deleted", { duration: 5000 });
  }

  async function publishTemplates() {
    const items = templates.filter((x) => selected.includes(x.id));
    const groupedBySku = groupByMultipleKeys(items, ["sku", "organization"]);
    const keys = Object.keys(groupedBySku);
    for (let i = 0; i < keys.length; i++) {
      const templatesGroup = groupedBySku[keys[i]];
      if (templatesGroup.length > 2) {
        return toast.error(
          "you may only execute a pair of templates of the same sku from the same campaign",
          { duration: 5000 }
        );
      }
      await runTemplate(templatesGroup[0].id);
    }
  }

  return (
    <div className="w-full p-8 m-4 border rounded">
      <div className="flex-1 min-w-0 w-full">
        <h4 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
          Design Product Templates
        </h4>
        <p className="italic text-xs">Limited to the last month of requests</p>
      </div>
      <div className="w-full flex justify-between items-center gap-4">
        <div className="space-x-4">
          <button
            onClick={() => setSelected(templates.map((x) => x.id))}
            className="bg-white border px-4 py-2 text-black my-4"
          >
            select all
          </button>
          <button
            onClick={() => setSelected([])}
            className="bg-white border px-4 py-2 text-black my-4"
          >
            de-select all
          </button>
        </div>
        <button
          onClick={() => deleteAllSelectedLicensing()}
          className="bg-black px-4 py-2 text-white my-4"
        >
          delete selected
        </button>
      </div>
      <div>
        <table className="table-fixed w-full border-collapse border border-deeppurple text-xs">
          <thead>
            <tr>
              <th className="border border-deeppurple">select</th>
              <th className="border border-deeppurple">id</th>
              <th className="border border-deeppurple">timestamp</th>
              <th className="border border-deeppurple">handle</th>
              <th className="border border-deeppurple">file</th>
              <th className="border border-deeppurple">design_area</th>
              <th className="border border-deeppurple">organization</th>
              <th className="border border-deeppurple">organization name</th>
              <th className="border border-deeppurple">sku</th>
              <th className="border border-deeppurple">stores</th>
              <th className="border border-deeppurple"></th>
              <th className="border border-deeppurple"></th>
            </tr>
          </thead>
          <tbody>
            {templates.map((template, index) => {
              return (
                <tr className="p-2 m-2 group" key={index}>
                  <td className="p-2 border border-deeppurple truncate group-hover:bg-gray-50">
                    <div className="w-full h-full flex justify-center items-center">
                      <input
                        id={`${template.id}`}
                        name={`${template.id}`}
                        type="checkbox"
                        checked={selected.includes(`${template.id}`)}
                        className="focus:ring-dustyrose h-4 w-4 text-dustyrose border-gray-300 rounded"
                        onChange={() => handleSelect(`${template.id}`)}
                      />
                    </div>
                  </td>
                  <TableRow tooltip={true} value={template.id} />
                  <TableRow
                    tooltip={true}
                    value={getTime(template.created_at)}
                  />
                  <TableRow
                    tooltip={true}
                    value={template.handle ? template.handle : ""}
                  />
                  <TableRow
                    tooltip={true}
                    value={template.file ? template.file : ""}
                  />
                  <TableRow
                    tooltip={true}
                    value={template.design_area ? template.design_area : ""}
                  />
                  <TableRow
                    tooltip={true}
                    value={template.organization ? template.organization : ""}
                  />
                  <TableRow
                    tooltip={true}
                    value={
                      template.org_names ? template.org_names.join(",") : ""
                    }
                  />
                  <TableRow
                    tooltip={true}
                    value={template.sku ? template.sku : ""}
                  />
                  <TableRow
                    tooltip={true}
                    value={template.stores ? template.stores.join(",") : ""}
                  />
                  <td className="p-2 border border-deeppurple truncate group-hover:bg-gray-200 cursor-pointer underline">
                    <button
                      onClick={() => runTemplate(template.id)}
                      className="underline"
                    >
                      Run
                    </button>
                  </td>
                  <td className="p-2 border border-deeppurple truncate group-hover:bg-gray-50">
                    <button
                      className="underline"
                      onClick={() => deleteTemplate(template.id)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>{" "}
      <nav
        className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6 mt-6"
        aria-label="Pagination"
      >
        <div className="hidden sm:block">
          <p className="text-sm text-gray-700">
            Showing page <span className="font-medium">{`${page}`}</span>
          </p>
        </div>
        <div className="flex flex-1 justify-between sm:justify-end">
          <button
            onClick={() => previousPage()}
            className="relative ml-3 inline-flex items-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus-visible:outline-offset-0"
          >
            Previous
          </button>
          <button
            onClick={() => nextPage()}
            className="relative ml-3 inline-flex items-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus-visible:outline-offset-0"
          >
            Next
          </button>
        </div>
      </nav>
      <div className="w-full flex justify-start">
        <div className="my-2 ml-2">
          <button
            onClick={() => publishTemplates()}
            className="bg-black px-4 py-2 text-white my-4"
          >
            Publish Selected Templates
          </button>
        </div>
      </div>
    </div>
  );
}
